import { signal, inject, Service } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import moment from 'moment';

import { Language, languages } from './languages.model';
import { HttpClient } from '@angular/common/http';

@Service()
export class I18nService {
  private http = inject(HttpClient);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);

  static readonly language = signal<Language>(null);
  private static readonly data = signal<{ [key: string]: string }>(null);

  constructor() {
    const locale: string = localStorage.getItem('locale');
    this.initLanguage(locale || 'en');
  }

  private fetch(locale: string) {
    this.http.get<{ [key: string]: string }>(`assets/langs/${locale}.json`).subscribe((data) => {
      I18nService.data.set(data);
    });
  }

  private initLanguage(locale: string) {
    const language: Language = languages.find((it) => it.key === locale);
    if (language) {
      this.applyLanguage(language);
    } else {
      throw new Error(`Incorrect locale used for I18nService: ${locale}`);
    }
  }

  isReady(): boolean {
    return !!I18nService.data();
  }

  setLanguage(language: Language) {
    localStorage.setItem('locale', language.key);

    this.applyLanguage(language);
  }

  private applyLanguage(language: Language) {
    const current = I18nService.language();
    if (!current || current.key !== language.key) {
      I18nService.language.set(language);
      moment.locale(language.key);
      this.dateAdapter.setLocale(language.key);
      this.fetch(language.key);
    }
  }

  getLanguage(): Language {
    return I18nService.language();
  }

  /**
   * A translation body could contain variables indexed by numbers.
   * Variables substitution used only if second argument (templateVars) provided.
   *
   * For example: const result = this.i18n.getTranslation('No such name ${0} in the list ${1}', ['username', 'userlist']);
   * The result will be 'No such name username in the list userlist'
   */
  public getTranslation(phrase: string, templateVars?: string[]): string {
    const dataMap = I18nService.data();
    const value = (dataMap && dataMap[phrase]) || phrase;
    if (templateVars) {
      return templateVars.reduce((result: string, templateVar: string, idx: number): string => {
        return result.replace(`\$\{${idx}\}`, templateVar);
      }, value);
    } else {
      return value;
    }
  }

  public getMomentLocaleKey() {
    let key = this.getLanguage().key;
    if (key === 'us') {
      key = 'en';
    }
    if (key === 'cn') {
      key = 'zh-cn';
    }
    return key;
  }
}
